import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api";
import { AUTH_LOGIN_ROLES, type AuthLoginRole } from "../../data/login-roles.data";
import type { LoginRequest } from "../../data/login-request.data";
import type { LoginResponseData, LoginResponseUser } from "../../data/login-response.data";
import { useAuthSession } from "../../shared/hooks/auth-session-context";
import { createLoginSchema } from "../schemas/login.schema";
import type { LoginFormMethods, LoginFormValues } from "../types/login.types";
import { authTokenService } from "../../../../lib/authTokenService";
import { useResultHandler } from "../../../../hooks/useResultHandler";
import { ResultCode } from "../../../../lib/result-codes";
import {
  getMerchantOnboardingPath,
  getMerchantOnboardingStage,
} from "../../merchant-register/utils/merchantOnboarding";
import { merchantOnboardingApi } from "../../merchant-register/api/merchantOnboardingApi";
import { MERCHANT_ONBOARDING_STAGES } from "../../shared";

const isMerchantOnboardingLogin = (
  result: LoginResponseData | null,
): result is LoginResponseData & { user: LoginResponseUser } =>
  result?.user?.role === "merchant" &&
  Boolean(result.access_token) &&
  Boolean(result.refresh_token);

interface UseLoginReturn {
  submitLogin: (email: string, password: string, onLoginSuccess?: () => void) => Promise<void>;
  loading: boolean;
  error: string | null;
  form: LoginFormMethods;
}

export const useLogin = (): UseLoginReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearSession, setSession } = useAuthSession();
  const { handleResult } = useResultHandler();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loginSchema = createLoginSchema(t);
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const submitLogin = useCallback(
    async (email: string, password: string, onLoginSuccess?: () => void) => {
      const request: LoginRequest = { provider: "email", email, password };
      setLoading(true);
      setError(null);

      try {
        const response = await authApi.login(request);
        const result = response.data;

        const merchantOnboardingStage = getMerchantOnboardingStage(response.code);
        const hasMerchantLoginSession = isMerchantOnboardingLogin(result);
        if (
          hasMerchantLoginSession &&
          (merchantOnboardingStage || response.code === ResultCode.Success)
        ) {
          setSession({
            user: result.user,
            token: result.access_token,
            refreshToken: result.refresh_token,
            merchantOnboardingStage:
              merchantOnboardingStage ?? MERCHANT_ONBOARDING_STAGES.businessDetails,
          });

          try {
            const onboardingStatus = await merchantOnboardingApi.getStatus();
            setSession({
              user: {
                ...result.user,
                is_onboarding_completed: onboardingStatus.is_onboarding_completed,
              },
              token: result.access_token,
              refreshToken: result.refresh_token,
              merchantOnboardingStage: onboardingStatus.stage,
            });
            authTokenService.resetSessionExpiredHandling();
            navigate(getMerchantOnboardingPath(onboardingStatus.stage), { replace: true });
          } catch (error) {
            clearSession();
            throw error;
          }

          return;
        }

        if (response.code !== ResultCode.Success) {
          handleResult(response);
          return;
        }

        const userRole = result?.user?.role as AuthLoginRole | undefined;
        if (
          !result ||
          !result.user ||
          !userRole ||
          !AUTH_LOGIN_ROLES.includes(userRole) ||
          !result.access_token ||
          !result.refresh_token
        ) {
          throw new Error("Login failed: Missing or invalid login data");
        }

        const provisionalMerchantOnboardingStage =
          result.user.role === "merchant" ? MERCHANT_ONBOARDING_STAGES.businessDetails : undefined;

        setSession({
          user: result.user,
          token: result.access_token,
          refreshToken: result.refresh_token,
          merchantOnboardingStage: provisionalMerchantOnboardingStage,
        });

        authTokenService.resetSessionExpiredHandling();
        if (onLoginSuccess) {
          onLoginSuccess();
          return;
        }

        navigate("/dashboard");
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Login failed");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [clearSession, handleResult, navigate, setSession],
  );

  return {
    submitLogin,
    loading,
    error,
    form,
  };
};
